import React, { useMemo, useState } from 'react';
import { useGetCardCaseLogsQuery } from '../../api/cardSlice';
import { NumericFormat } from 'react-number-format';
import { Stack, Typography, Grid, Button } from '@mui/material';
import { ResponsiveAreaBump } from '@nivo/bump';
import { Box } from '@mui/system';
import CaseUsageExploration from './CaseUsageExploration';
import LoadingIndicator from '../LoadingIndicator';
import CardItemDetails from './CardItemDetails';
import StackedBarCardUsageChart from './StackedBarCardUsageChart';


function CardUsageGraph({ card }) {
    const { data: caseLogs = [], isLoading, isSuccess } = useGetCardCaseLogsQuery({ cardId: card.id });
    const [logsForExploration, setLogsForExploration] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isShowingCardItemDetails, setIsShowingCardItemDetails] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const sortedCases = useMemo(() => {
        return caseLogs.slice().sort((a, b) => new Date(a.date_of_service) - new Date(b.date_of_service));
    }, [caseLogs]);

    const { categoryData, categories, areaBumpData, barData, avgPerCaseDisplay, projectedPerCaseDisplay, topCategories } = useMemo(() => {
        // Get all unique categories with their total costs
        const categoryTotals = {};
        caseLogs.forEach(caseData => {
            caseData.logs.forEach(log => {
                const category = log.item.category || 'Uncategorized';
                const cost = (log.used_quantity * log.item.cost) / 100;
                categoryTotals[category] = (categoryTotals[category] || 0) + cost;
            });
        });

        // Sort categories by total cost and get top 10
        const sortedCategories = Object.entries(categoryTotals)
            .sort(([,a], [,b]) => b - a)
            .map(([category]) => category);
        
        const top10Categories = sortedCategories.slice(0, 10);
        const otherCategories = sortedCategories.slice(10);
        const shouldShowOther = otherCategories.length > 0;
        
        // Final categories list (top 10 + "Other" if needed)
        const finalCategories = shouldShowOther ? [...top10Categories, 'Other'] : top10Categories;

        // Helper function to get category for data processing
        const getCategoryForData = (originalCategory) => {
            if (top10Categories.includes(originalCategory)) {
                return originalCategory;
            }
            return shouldShowOther ? 'Other' : originalCategory;
        };

        // Create data series for each category (for line chart)
        const categorySeriesData = finalCategories.map((category, index) => ({
            id: category,
            color: selectedCategories.length > 0 && !selectedCategories.includes(category)
                ? `hsl(${(index * 137.5) % 360}, 30%, 80%)` // Faded color
                : `hsl(${(index * 137.5) % 360}, 70%, 50%)`, // Normal color
            data: sortedCases.map((caseData) => ({
                x: new Date(caseData.date_of_service),
                y: caseData.logs
                    .filter(log => getCategoryForData(log.item.category || 'Uncategorized') === category)
                    .reduce((total, log) => total + (log.used_quantity * log.item.cost) / 100, 0),
            })),
        }));

        // Create data for Stream chart (one point per case, x is date string)
        const streamChartData = sortedCases.map((caseData) => {
            const dataPoint = {
                x: caseData.date_of_service,
            };
            finalCategories.forEach(category => {
                dataPoint[category] = caseData.logs
                    .filter(log => getCategoryForData(log.item.category || 'Uncategorized') === category)
                    .reduce((total, log) => total + (log.used_quantity * log.item.cost) / 100, 0);
            });
            return dataPoint;
        });

        // Create data for Bar chart: keep unique index per case using `caseLabel`, and store raw date on `_date`
        const barChartData = sortedCases.map((caseData, idx) => {
            const dataPoint = {
                caseLabel: `case-${idx}`,
                _date: caseData.date_of_service,
            };
            finalCategories.forEach(category => {
                dataPoint[category] = caseData.logs
                    .filter(log => getCategoryForData(log.item.category || 'Uncategorized') === category)
                    .reduce((total, log) => total + (log.used_quantity * log.item.cost) / 100, 0);
            });
            return dataPoint;
        });

        // Create data for AreaBump chart
        const areaBumpChartData = finalCategories.map((category, index) => ({
            id: category,
            color: selectedCategories.length > 0 && !selectedCategories.includes(category)
                ? `hsl(${(index * 137.5) % 360}, 30%, 80%)` // Faded color
                : `hsl(${(index * 137.5) % 360}, 70%, 50%)`, // Normal color
            data: sortedCases.map((caseData, idx) => ({
                x: `case-${idx}`,
                y: caseData.logs
                    .filter(log => getCategoryForData(log.item.category || 'Uncategorized') === category)
                    .reduce((total, log) => total + (log.used_quantity * log.item.cost) / 100, 0),
            })),
        }));

        // compute overall and per-category averages and projected values
        const totalCases = sortedCases.length || 0;
        const totalAll = sortedCases.reduce((sum, caseData) => sum + caseData.logs.reduce((s, log) => s + (log.used_quantity * log.item.cost) / 100, 0), 0);
        const overallAvg = totalCases ? totalAll / totalCases : 0;
        const projectedOverall = card && card.factored_cost ? card.factored_cost / 100 : 0;

        let avgPerCaseDisplay = overallAvg;
        let projectedPerCaseDisplay = projectedOverall;

        if (selectedCategories && selectedCategories.length > 0) {
            const perCaseCatTotals = sortedCases.map(caseData => caseData.logs.reduce((s, log) => {
                const cat = getCategoryForData(log.item.category || 'Uncategorized');
                return s + (selectedCategories.includes(cat) ? (log.used_quantity * log.item.cost) / 100 : 0);
            }, 0));
            const sumCat = perCaseCatTotals.reduce((a, b) => a + b, 0);
            const avgCat = totalCases ? sumCat / totalCases : 0;
            avgPerCaseDisplay = avgCat;
            const proportion = overallAvg > 0 ? (avgCat / overallAvg) : 0;
            projectedPerCaseDisplay = projectedOverall * proportion;
        }

        return { 
            categoryData: categorySeriesData, 
            categories: finalCategories, 
            streamData: streamChartData,
            areaBumpData: areaBumpChartData,
            barData: barChartData,
            topCategories: top10Categories,
            avgPerCaseDisplay,
            projectedPerCaseDisplay
        };
    }, [caseLogs, sortedCases, selectedCategories]);

    const handleCategoryClick = (category) => {
        setSelectedCategories(prev => {
            if (!prev) return [category];
            const exists = prev.includes(category);
            if (exists) return prev.filter(c => c !== category);
            return [...prev, category];
        });
    }
    
    return (
        <Stack>
            <Typography variant="h6">Card Usage Analysis by Item Category</Typography>
            { isLoading && <LoadingIndicator /> }
            { isSuccess &&
                <Grid container spacing={3} pb={3}>
                    <Grid item xs={12}>
                        <Typography variant='h6'>Item Categories</Typography>
                        <Stack direction='row' py={2} spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 2 }}>
                            { categoryData.map((category) => (
                                <Box 
                                    key={category.id}
                                    onClick={() => handleCategoryClick(category.id)}
                                    sx={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '4px 8px',
                                        backgroundColor: category.color,
                                        color: '#fff',
                                        borderRadius: '4px',
                                        fontSize: '0.875rem',
                                        fontWeight: 'bold',
                                        border: selectedCategories && selectedCategories.includes(category.id) ? '2px solid #000' : '2px solid transparent',
                                        opacity: selectedCategories && selectedCategories.length > 0 && !selectedCategories.includes(category.id) ? 0.5 : 1,
                                        '&:hover': {
                                            opacity: 0.8,
                                            cursor: 'pointer'
                                        }
                                    }}
                                >
                                    {category.id}
                                </Box>
                            )) }
                            <Button onClick={() => setSelectedCategories([])} size="small" sx={{ ml: 1 }}>Clear</Button>
                        </Stack>
                        <Box
                            sx={{
                                borderRadius: '4px',
                                padding: '4px 8px',
                                backgroundColor: '#eee',
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant='body2' sx={{ fontWeight: 'bold', marginRight: 1 }}>
                                Average Spend per Case: <NumericFormat
                                    value={avgPerCaseDisplay}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                    prefix={'$'}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                />
                            </Typography>
                            <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                                Expected Spend per Case: <NumericFormat
                                    value={projectedPerCaseDisplay}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                    prefix={'$'}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                />
                            </Typography>
                        </Box>
                    </Grid>
                    {/* Stacked Bar Chart */}
                    <Grid item xs={12}>
                        <Typography variant="h6">Stacked Bar Chart</Typography>
                        <Box height={400}>
                            <StackedBarCardUsageChart
                                barData={barData}
                                categories={categories}
                                selectedCategories={selectedCategories}
                                sortedCases={sortedCases}
                                setLogsForExploration={setLogsForExploration}
                                projectedPerCaseDisplay={projectedPerCaseDisplay}
                                avgPerCaseDisplay={avgPerCaseDisplay}
                                card={card}
                            />
                        </Box>
                    </Grid>
                    {/* Area Bump Chart */}
                    <Grid item xs={12}>
                        <Typography variant="h6">Area Bump Chart</Typography>
                        <Box height={400}>
                            <ResponsiveAreaBump
                                height={400}
                                data={areaBumpData}
                                margin={{ top: 40, right: 110, bottom: 40, left: 75 }}
                                spacing={8}
                                colors={{ datum: 'color' }}
                                blendMode="multiply"
                                defs={[
                                    {
                                        id: 'dots',
                                        type: 'patternDots',
                                        background: 'inherit',
                                        color: '#38bcb2',
                                        size: 4,
                                        padding: 1,
                                        stagger: true
                                    },
                                    {
                                        id: 'lines',
                                        type: 'patternLines',
                                        background: 'inherit',
                                        color: '#eed312',
                                        rotation: -45,
                                        lineWidth: 6,
                                        spacing: 10
                                    }
                                ]}
                                startLabel="id"
                                endLabel="id"
                                axisTop={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: '',
                                    legendPosition: 'middle',
                                    legendOffset: -36,
                                    format: (value) => {
                                        try {
                                            const m = (value || '').toString().match(/case-(\d+)/);
                                            const d = m ? new Date(sortedCases[parseInt(m[1], 10)]?.date_of_service) : new Date(value);
                                            return isNaN(d) ? value : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                                        } catch (e) {
                                            return value;
                                        }
                                    }
                                }}
                                axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Date',
                                    legendPosition: 'middle',
                                    legendOffset: 32,
                                    format: (value) => {
                                        try {
                                            const m = (value || '').toString().match(/case-(\d+)/);
                                            const d = m ? new Date(sortedCases[parseInt(m[1], 10)]?.date_of_service) : new Date(value);
                                            return isNaN(d) ? value : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                                        } catch (e) {
                                            return value;
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            }
            { logsForExploration.length > 0 &&
                <CaseUsageExploration 
                    caseItemLogs={logsForExploration}
                    cardItems={card.pick_list}
                    recommendations={card.recommendations}
                    selectedCategories={selectedCategories}
                    topCategories={topCategories}
                    setSelectedItem={(item) => {
                        setSelectedItem(item);
                        setIsShowingCardItemDetails(true);
                    }}
                />
            }
            <CardItemDetails 
                open={isShowingCardItemDetails}
                onClose={() => setIsShowingCardItemDetails(false)}
                item={selectedItem}
                cardItem={card.pick_list[selectedItem?.category]?.find(i => i.id === selectedItem?.id)}
                recommendation={card.recommendations.find(r => r.item.id === selectedItem?.id)}
                cases={sortedCases}
            />
        </Stack>
    );
}

export default CardUsageGraph;
